import { getSchema, PasteRule } from "@tiptap/core"
import { gitHubEmojis, shortcodeToEmoji } from "@tiptap/extension-emoji"
import {
  BulletList as ReferenceBulletList,
  ListItem as ReferenceListItem,
  OrderedList as ReferenceOrderedList,
} from "@tiptap/extension-list"

import {
  attrsToHtmlAttributes,
  serializeNodeToHtml,
  setMarkdownSchema,
} from "@/tiptap/core/markdown/markdown-schema"

// Keep in sync with `buildEditorExtensions` — we only convert shortcodes the
// editor itself can insert, so `:regional_indicator_a:` stays plain text.
const MARKDOWN_EMOJIS = gitHubEmojis.filter((emoji) => !emoji.name.includes("regional"))
const EMOJI_SHORTCODE_RE = /^:([a-zA-Z0-9_+-]+):/
// Also match a shortcode immediately after another (`:joy::rocket:`). The
// stock TipTap paste rule requires `(^|\s)`, so only the first on a line
// converted when shortcodes were concatenated.
const EMOJI_PASTE_RE = /(^|\s|(?<=:)):([a-zA-Z0-9_+-]+):/g

// Maps Hudu callout variants to GitHub-flavored markdown alert labels, and the
// reverse so `> [!WARNING]` paste/import rebuilds the matching callout.
const CALLOUT_ALERT_LABELS = {
  info: "NOTE",
  success: "TIP",
  warning: "WARNING",
  danger: "CAUTION",
}

const ALERT_LABEL_TO_VARIANT = {
  NOTE: "info",
  TIP: "success",
  IMPORTANT: "warning",
  WARNING: "warning",
  CAUTION: "danger",
}

const GFM_ALERT_LABELS = Object.keys(ALERT_LABEL_TO_VARIANT).join("|")
const GFM_ALERT_HEADER_RE = new RegExp(
  `^ {0,3}>[ \\t]*\\[!(${GFM_ALERT_LABELS})\\][ \\t]?([^\\n]*)(?:\\n|$)`,
  "i",
)
const GFM_ALERT_START_RE = new RegExp(
  `(?:^|\\n) {0,3}>[ \\t]*\\[!(${GFM_ALERT_LABELS})\\]`,
  "i",
)

const MARKDOWN_CONFIG_FIELDS = [
  "markdownTokenName",
  "markdownTokenizer",
  "parseMarkdown",
  "renderMarkdown",
  "markdownOptions",
]

// The list handlers in @tiptap/extension-list are pure config (they only use
// the provided helpers, never `this`), so we can lift them onto Hudu's
// Enhanced* list extensions — which extend the older singular list packages
// that predate markdown support — without changing any editing behavior.
function liftMarkdownConfig(referenceExtension) {
  const config = {}

  MARKDOWN_CONFIG_FIELDS.forEach((field) => {
    const value = referenceExtension?.config?.[field]
    if (value !== undefined) config[field] = value
  })

  return config
}

// Emit a block-level HTML-preservation node as its canonical HTML. Markdown
// treats standalone HTML as an html block, and the parser rebuilds the node
// from its parseHTML rules, so the round-trip is lossless.
function htmlBlockRenderMarkdown(node) {
  return serializeNodeToHtml(node)
}

// Wrap an inline mark's children in its HTML tag. The serializer calls this
// with a placeholder for the children and extracts the opening/closing tags
// around it, so inline markdown inside the mark is still rendered.
function inlineMarkRenderMarkdown(tag, { includeAttrs = false } = {}) {
  return (node, helpers) => {
    const attrs = includeAttrs ? attrsToHtmlAttributes(node.attrs) : ""
    return `<${tag}${attrs}>${helpers.renderChildren()}</${tag}>`
  }
}

function calloutRenderMarkdown(node, helpers) {
  const label = CALLOUT_ALERT_LABELS[node?.attrs?.variant] || "NOTE"
  const body = helpers.renderChildren(node.content, "\n\n")
  const quoted = body
    .split("\n")
    .map((line) => (line.length ? `> ${line}` : ">"))
    .join("\n")

  return `> [!${label}]\n${quoted}`
}

function stripBlockquotePrefix(line) {
  return line.replace(/^ {0,3}>[ \t]?/, "")
}

// Pull consecutive `>`-prefixed lines off the start of `src`. Unquoted lines
// (including a blank line with no `>`) end the alert, matching CommonMark
// blockquote termination so a following paragraph is not swallowed.
function consumeQuotedLines(src) {
  let consumed = ""
  let body = ""
  let remaining = src

  while (remaining.length > 0) {
    const newlineIndex = remaining.indexOf("\n")
    const line = newlineIndex === -1 ? remaining : remaining.slice(0, newlineIndex)
    const lineWithBreak = newlineIndex === -1 ? remaining : remaining.slice(0, newlineIndex + 1)

    if (!/^ {0,3}>/.test(line)) break

    consumed += lineWithBreak
    body += `${body ? "\n" : ""}${stripBlockquotePrefix(line)}`
    remaining = remaining.slice(lineWithBreak.length)

    if (newlineIndex === -1) break
  }

  return { consumed, body }
}

function tokenizeCalloutBody(body, lexer) {
  if (!body.trim()) return []

  const tokens = lexer.blockTokens(body)

  tokens.forEach((token) => {
    if (token.text && (!token.tokens || token.tokens.length === 0)) {
      token.tokens = lexer.inlineTokens(token.text)
    }
  })

  return tokens
}

function calloutParseMarkdown(token, helpers) {
  const parseBlockChildren = helpers.parseBlockChildren ?? helpers.parseChildren
  const content = parseBlockChildren(token.tokens || [])

  return helpers.createNode(
    "callout",
    { variant: token.variant || "info" },
    content.length > 0 ? content : [helpers.createNode("paragraph")],
  )
}

function emojiParseMarkdown(token, helpers) {
  return helpers.createNode("emoji", { name: token.name })
}

const emojiMarkdownTokenizer = {
  name: "emoji",
  level: "inline",
  start(src) {
    const match = src.match(/:([a-zA-Z0-9_+-]+):/)
    return match ? match.index : -1
  },
  tokenize(src) {
    const match = src.match(EMOJI_SHORTCODE_RE)
    if (!match) return undefined

    const emojiItem = shortcodeToEmoji(match[1], MARKDOWN_EMOJIS)
    if (!emojiItem) return undefined

    return {
      type: "emoji",
      raw: match[0],
      name: emojiItem.name,
    }
  },
}

const calloutMarkdownTokenizer = {
  name: "callout",
  level: "block",
  start(src) {
    const match = src.match(GFM_ALERT_START_RE)
    return match ? match.index : -1
  },
  tokenize(src, _tokens, lexer) {
    const header = src.match(GFM_ALERT_HEADER_RE)
    if (!header) return undefined

    const variant = ALERT_LABEL_TO_VARIANT[header[1].toUpperCase()] || "info"
    const sameLineBody = header[2].trim()
    const afterHeader = src.slice(header[0].length)
    const quoted = consumeQuotedLines(afterHeader)
    const body = [sameLineBody, quoted.body].filter(Boolean).join("\n")

    return {
      type: "callout",
      raw: header[0] + quoted.consumed,
      variant,
      tokens: tokenizeCalloutBody(body, lexer),
    }
  },
}

// Extension name -> markdown config to merge in via `.extend()`. Only covers
// nodes/marks that Tiptap's base extensions do not already handle. Standard
// nodes (paragraph, heading, bold, italic, strike, code, link, blockquote,
// code block, table, task list, image, highlight, math, hr, hard break)
// inherit handlers from their base @tiptap extensions. Emoji already serializes
// as `:shortcode:` but does not parse it, so we attach a tokenizer below.
function buildHandlerMap() {
  const inlinePreservationMarks = ["span", "cite", "dfn", "ins", "kbd", "label"]
  const blockPreservationNodes = [
    "div",
    "divInline",
    "footer",
    "dd",
    "htmlFigure",
    "figure",
    "iframe",
    "audio",
    "video",
    "youtube",
    "button",
    "input",
    "select",
    "option",
    "details",
    "detailsSummary",
    "detailsContent",
    "tocNode",
  ]

  const handlers = {
    bulletList: liftMarkdownConfig(ReferenceBulletList),
    orderedList: liftMarkdownConfig(ReferenceOrderedList),
    listItem: liftMarkdownConfig(ReferenceListItem),
    callout: {
      parseMarkdown: calloutParseMarkdown,
      markdownTokenizer: calloutMarkdownTokenizer,
      renderMarkdown: calloutRenderMarkdown,
    },
    emoji: {
      parseMarkdown: emojiParseMarkdown,
      markdownTokenizer: emojiMarkdownTokenizer,
      addPasteRules() {
        const emojis = this.options.emojis
        const typeName = this.name

        return [
          new PasteRule({
            find: EMOJI_PASTE_RE,
            handler: ({ range, match, chain }) => {
              const prefix = match[1] || ""
              const name = match[2]
              if (!shortcodeToEmoji(name, emojis)) return

              const shortcodeFrom = range.from + prefix.length

              chain()
                .insertContentAt(
                  { from: shortcodeFrom, to: range.to },
                  { type: typeName, attrs: { name } },
                  { updateSelection: false },
                )
                .command(({ tr, state }) => {
                  tr.setStoredMarks(state.doc.resolve(state.selection.to - 1).marks())
                  return true
                })
                .run()
            },
          }),
        ]
      },
    },
    superscript: { renderMarkdown: inlineMarkRenderMarkdown("sup") },
    subscript: { renderMarkdown: inlineMarkRenderMarkdown("sub") },
  }

  inlinePreservationMarks.forEach((name) => {
    handlers[name] = {
      renderMarkdown: inlineMarkRenderMarkdown(name, { includeAttrs: true }),
    }
  })

  blockPreservationNodes.forEach((name) => {
    handlers[name] = { renderMarkdown: htmlBlockRenderMarkdown }
  })

  return handlers
}

const HANDLER_MAP = buildHandlerMap()

// Attach markdown handlers to the editor extension set and prime the schema
// used for HTML-preservation serialization. Called with the fully-assembled
// extension array (including the Markdown extension itself).
export function applyMarkdownHandlers(extensions) {
  const mapped = extensions.map((extension) => {
    if (!extension?.name) return extension

    const config = HANDLER_MAP[extension.name]
    return config ? extension.extend(config) : extension
  })

  try {
    setMarkdownSchema(getSchema(mapped))
  } catch {
    // Schema priming is best-effort; HTML-preservation fallback simply no-ops
    // if the schema is unavailable.
  }

  return mapped
}

export { CALLOUT_ALERT_LABELS }

export default applyMarkdownHandlers
